/**
 * Public menu hook — fetches the menu tree for frontstore navigation.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MenuItem {
  id: number
  label: string
  link_type: 'category' | 'custom_url' | 'internal_page'
  link_target: string | null
  sort_order: number
  category_slug?: string
  children: MenuItem[]
}

export interface MenuTree {
  id: number
  name: string
  slug: string
  items: MenuItem[]
}

export interface UsePublicMenuResult {
  menu: MenuTree | null
  isLoading: boolean
  isError: boolean
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePublicMenu(slug: string): UsePublicMenuResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-menu', slug],
    queryFn: async () => {
      const { data: response } = await api.get<{ data: MenuTree }>(`/menus/${slug}`)
      return response.data
    },
    enabled: !!slug,
    staleTime: 60 * 1000, // 1 minute
    retry: false,
  })

  return {
    menu: data ?? null,
    isLoading,
    isError,
  }
}
